// ============================================
// COMMUNITY HUB — MAIN ORCHESTRATOR
// Pro/Enterprise Only — Community Platform
// ============================================

import React, { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import { 
  Plus, X, AlertCircle, CheckCircle, Image as ImageIcon, ArrowLeft, Heart, MessageSquare, 
  Trash2, Pin, Share2, Search, Users, Clock, TrendingUp, Paperclip,
  Globe, Lock, Edit2, MapPin, Calendar, DollarSign, Crown, BadgeCheck, 
  BarChart3, Settings, AlertTriangle, Send
} from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import './styles.css';
import {
  CommunityCard,
  PostCard,
  SearchFilterBar,
  SortBar,
  CreateCommunityModal,
  EmptyState,
  ToastContainer,
  Avatar,
  Badge,
  Skeleton,
  SocialLinksManager,
  EarningsDashboard,
} from './components';
import { useAuth, usePermissions, useCommunities, useCommunityDetail, useToast, useInfiniteScroll, useDebounce } from './hooks';
import { communityApi, postApi, monetizationApi, uploadApi } from './api';
import { sortPosts, validateFile } from './utils';
import type {
  Community, Post, SortOption, FilterOption,
  CommunityCategory, EarningsSummary,
} from './types';

// ============================================
// MAIN COMPONENT
// ============================================
const CommunityHub: React.FC<{ userPlan?: string }> = ({ userPlan = 'starter' }) => {
  // ─── Hooks ───────────────────────────
  const { user, getCurrentUserId, isOwner } = useAuth();
  const permissions = usePermissions(user);
  const { toasts, success, error, warning, removeToast } = useToast();

  // ─── State ───────────────────────────
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [selectedCommunity, setSelectedCommunity] = useState<Community | null>(null);
  const [activeSort, setActiveSort] = useState<SortOption>('latest');
  const [activeFilter, setActiveFilter] = useState<FilterOption>('all');
  const [activeCategory, setActiveCategory] = useState<string>('all');
  const [searchInput, setSearchInput] = useState('');
  const [showMembers, setShowMembers] = useState(false);
  const [showEarnings, setShowEarnings] = useState(false);
  const [showSettings, setShowSettings] = useState(false);
  const [earningsSummary, setEarningsSummary] = useState<EarningsSummary | null>(null);
  const [earningsLoading, setEarningsLoading] = useState(false);

  const debouncedSearch = useDebounce(searchInput, 300);

  // ─── Data Fetching ───────────────────
  const { communities, loading, error: listError, hasMore, fetchCommunities, loadMore } = useCommunities();
  const {
    community: detailCommunity,
    posts,
    loading: detailLoading,
    error: detailError,
    isMember,
    isAdmin,
    fetchDetail,
    addPost,
    updatePost,
    removePost,
    updateCommunity,    // ← Check if this exists
    setIsMember,
  } = useCommunityDetail(selectedCommunity?._id || null);
  // Add navigate
const navigate = useNavigate();

  // ─── Infinite Scroll ────────────────
  const scrollTargetRef = useInfiniteScroll(loadMore, {
    enabled: !selectedCommunity && hasMore && !loading,
    threshold: 0.3,
  });

  // ─── Current User ID ────────────────
  const currentUserId = getCurrentUserId();

  // ─── Initial Fetch ──────────────────
  useEffect(() => {
    fetchCommunities({ reset: true });
  }, []);

  // ─── Search/Category Effect ─────────
  useEffect(() => {
    fetchCommunities({
      search: debouncedSearch,
      category: activeCategory,
      filter: activeFilter,
      reset: true,
    });
  }, [debouncedSearch, activeCategory, activeFilter]);

  // ─── Sorted Posts ───────────────────
  const sortedPosts = useMemo(() => {
    if (!posts) return [];
    return sortPosts(posts, activeSort, currentUserId);
  }, [posts, activeSort, currentUserId]);

  // ─── Handlers ───────────────────────
  const handleCreateCommunity = useCallback(async (data: any) => {
    try {
      const res = await communityApi.createCommunity(data);
      if (res.success) {
        success('Community created successfully! 🎉');
        fetchCommunities({ reset: true });
      } else {
        error(res.error || 'Failed to create community');
      }
    } catch (err: any) {
      error(err.message || 'Something went wrong');
    }
  }, [success, error, fetchCommunities]);

  const handleJoinCommunity = useCallback(async () => {
    if (!selectedCommunity) return;
    
    if (selectedCommunity.isMonetized && selectedCommunity.subscriptionPrice > 0) {
      navigate('/payment', {
        state: {
          plan: 'community',
          billingCycle: 'monthly',
          email: user?.email || '',
          amount: selectedCommunity.subscriptionPrice / 100,
          communityId: selectedCommunity._id,
          communityName: selectedCommunity.name,
          isCommunityPayment: true,
        }
      });
      return;
    }
    
    try {
      await communityApi.joinCommunity(selectedCommunity._id);
      setIsMember(true);
      updateCommunity({ memberCount: (selectedCommunity.memberCount || 0) + 1 });
      success('Welcome to the community! 👋');
    } catch (err: any) {
      error(err.message || 'Failed to join');
    }
  }, [selectedCommunity, user, navigate, success, error, setIsMember, updateCommunity]);
  
  const handleLeaveCommunity = useCallback(async () => {
    if (!selectedCommunity) return;
    if (!window.confirm('Leave this community?')) return;
    try {
      await communityApi.leaveCommunity(selectedCommunity._id);
      setIsMember(false);
      updateCommunity({ memberCount: Math.max(0, (selectedCommunity.memberCount || 0) - 1) });
      success('You left the community');
    } catch (err: any) {
      error(err.message || 'Failed to leave');
    }
  }, [selectedCommunity, success, error, updateCommunity, setIsMember]);

  const handleLike = useCallback(async (postId: string) => {
    try {
      await postApi.toggleLike(postId);
      fetchDetail();
    } catch (err: any) {
      error('Failed to like post');
    }
  }, [fetchDetail, error]);

  const handleComment = useCallback(async (postId: string) => {
    // Comment is handled inside PostCard via commentApi
    fetchDetail();
  }, [fetchDetail]);

  const handlePin = useCallback(async (postId: string) => {
    try {
      await postApi.togglePin(postId);
      fetchDetail();
      success('Post pin status updated');
    } catch (err: any) {
      error('Failed to pin post');
    }
  }, [fetchDetail, success, error]);

  const handleDeletePost = useCallback(async (postId: string) => {
    try {
      await postApi.deletePost(postId);
      removePost(postId);
      success('Post deleted');
    } catch (err: any) {
      error('Failed to delete post');
    }
  }, [removePost, success, error]);

  const handleEditPost = useCallback(async (postId: string, content: string) => {
    try {
      await postApi.updatePost(postId, { content });
      updatePost(postId, { content, edited: true, editedAt: new Date().toISOString() });
      success('Post updated');
    } catch (err: any) {
      error('Failed to edit post');
    }
  }, [updatePost, success, error]);

  const handleVote = useCallback(async (postId: string, optionId: string) => {
    try {
      await postApi.votePoll(postId, optionId);
      fetchDetail();
    } catch (err: any) {
      error('Failed to vote');
    }
  }, [fetchDetail, error]);

  const handleRSVP = useCallback(async (postId: string) => {
    try {
      await postApi.rsvpEvent(postId);
      fetchDetail();
      success('RSVP confirmed!');
    } catch (err: any) {
      error('Failed to RSVP');
    }
  }, [fetchDetail, success, error]);

  const handleShare = useCallback(() => {
    if (!selectedCommunity) return;
    const url = `${window.location.origin}/community/join/${selectedCommunity._id}`;
    navigator.clipboard.writeText(url);
    success('Invite link copied to clipboard! 📋');
  }, [selectedCommunity, success]);

  const handleReport = useCallback(async (postId: string) => {
    const reason = window.prompt('Why are you reporting this post?');
    if (!reason) return;
    try {
      await postApi.reportPost(postId, reason);
      success('Report submitted. We will review it.');
    } catch (err: any) {
      error('Failed to submit report');
    }
  }, [success, error]);

  const handleDeleteCommunity = useCallback(async () => {
    if (!selectedCommunity) return;
    if (!window.confirm('Permanently delete this community? This cannot be undone.')) return;
    try {
      await communityApi.deleteCommunity(selectedCommunity._id);
      success('Community deleted');
      setSelectedCommunity(null);
      fetchCommunities({ reset: true });
    } catch (err: any) {
      error('Failed to delete community');
    }
  }, [selectedCommunity, success, error, fetchCommunities]);

  const handleFetchEarnings = useCallback(async () => {
    if (!selectedCommunity) return;
    setEarningsLoading(true);
    try {
      const res = await monetizationApi.getEarningsSummary(selectedCommunity._id, 'monthly');
      if (res.success && res.data) {
        setEarningsSummary(res.data);
      }
    } catch (err: any) {
      error('Failed to load earnings');
    } finally {
      setEarningsLoading(false);
    }
  }, [selectedCommunity, error]);

  const handleUpdateSocialLinks = useCallback(async (links: any[]) => {
    if (!selectedCommunity) return;
    try {
      await communityApi.updateSocialLinks(selectedCommunity._id, links);
      success('Social links updated');
    } catch (err: any) {
      error('Failed to update social links');
    }
  }, [selectedCommunity, success, error]);

  // ─── RENDER: Community Detail View ───
  if (selectedCommunity && detailCommunity) {
    const plan = user?.plan || userPlan;
    const communityOwnerId = typeof detailCommunity.owner === 'string' ? detailCommunity.owner : detailCommunity.owner?._id;
    const isCommunityOwner = currentUserId === communityOwnerId;

    return (
      <div className="community-hub" style={{ maxWidth: 'var(--max-width-detail, 800px)' }}>
        {/* Toast Container */}
        <ToastContainer toasts={toasts} onRemove={removeToast} />

        {/* Back Button */}
        <button
          onClick={() => { setSelectedCommunity(null); setShowMembers(false); setShowEarnings(false); }}
          style={{
            background: 'none', border: 'none', cursor: 'pointer',
            display: 'flex', alignItems: 'center', gap: '8px',
            color: 'var(--text-secondary, #6b7280)', marginBottom: '20px',
            fontSize: '14px', fontWeight: '500', padding: '8px 12px',
            borderRadius: '8px', transition: 'background 0.15s',
          }}
        >
          <ArrowLeft size={18} /> Back to Communities
        </button>

        {/* Community Header */}
        <div style={{
          background: 'var(--accent-gradient, linear-gradient(135deg, #1e1b4b, #312e81))',
          padding: '32px', borderRadius: '18px', marginBottom: '24px', color: 'white',
        }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', flexWrap: 'wrap', gap: '16px' }}>
            <div style={{ flex: 1 }}>
              <div style={{ display: 'flex', gap: '8px', marginBottom: '8px', flexWrap: 'wrap' }}>
                <Badge
                  text={detailCommunity.visibility === 'private' ? 'Private' : 'Public'}
                  icon={detailCommunity.visibility === 'private' ? <Lock size={12} /> : <Globe size={12} />}
                  color="white"
                  bgColor="rgba(255,255,255,0.15)"
                />
                <Badge
                  text={detailCommunity.category || 'General'}
                  color="white"
                  bgColor="rgba(255,255,255,0.15)"
                />
                {detailCommunity.isMonetized && (
                  <Badge
                    text={`$${(detailCommunity.subscriptionPrice || 0) / 100}/mo`}
                    color="#fbbf24"
                    bgColor="rgba(251,191,36,0.2)"
                    icon={<DollarSign size={12} />}
                  />
                )}
              </div>
              <h1 style={{ fontSize: '28px', fontWeight: '700', marginTop: '12px', marginBottom: '8px' }}>
                {detailCommunity.name}
              </h1>
              <p style={{ opacity: 0.85, marginBottom: '16px', fontSize: '14px', lineHeight: '1.6' }}>
                {detailCommunity.description || 'Welcome to the community!'}
              </p>
              <div style={{ display: 'flex', gap: '20px', fontSize: '13px', opacity: 0.9 }}>
                <span style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                  <Users size={14} /> {detailCommunity.memberCount || 0} members
                </span>
                <span style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                  <MessageSquare size={14} /> {posts?.length || 0} posts
                </span>
              </div>
            </div>

            {/* Action Buttons */}
            <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap' }}>
              {isMember ? (
                <>
                  <button
                    onClick={() => { setShowMembers(!showMembers); setShowEarnings(false); setShowSettings(false); }}
                    style={{
                      padding: '10px 18px', background: 'rgba(255,255,255,0.12)',
                      border: '1px solid rgba(255,255,255,0.25)', color: 'white',
                      borderRadius: '10px', cursor: 'pointer', fontWeight: '600', fontSize: '13px',
                    }}
                  >
                    <Users size={14} style={{ marginRight: '6px' }} /> Members
                  </button>
                        <button onClick={handleShare} style={{ padding: '10px 18px', background: 'rgba(255,255,255,0.12)', border: '1px solid rgba(255,255,255,0.25)', color: 'white', borderRadius: '10px', cursor: 'pointer', fontWeight: '600', fontSize: '13px', display: 'flex', alignItems: 'center', gap: '6px' }}>
  <Share2 size={14} /> Invite
</button>


                  {(isCommunityOwner || isAdmin) && (
                    <button
                      onClick={() => { setShowSettings(!showSettings); setShowMembers(false); setShowEarnings(false); }}
                      style={{
                        padding: '10px 18px', background: 'rgba(255,255,255,0.12)',
                        border: '1px solid rgba(255,255,255,0.25)', color: 'white',
                        borderRadius: '10px', cursor: 'pointer', fontWeight: '600', fontSize: '13px',
                      }}
                    >
                      <Settings size={14} style={{ marginRight: '6px' }} /> Settings
                    </button>
                  )}
                  {detailCommunity.isMonetized && isCommunityOwner && (
                    <button
                      onClick={() => { setShowEarnings(!showEarnings); setShowMembers(false); setShowSettings(false); handleFetchEarnings(); }}
                      style={{
                        padding: '10px 18px', background: 'rgba(255,255,255,0.12)',
                        border: '1px solid rgba(255,255,255,0.25)', color: 'white',
                        borderRadius: '10px', cursor: 'pointer', fontWeight: '600', fontSize: '13px',
                      }}
                    >
                      <BarChart3 size={14} style={{ marginRight: '6px' }} /> Earnings
                    </button>
                  )}
                  <button
                    onClick={handleLeaveCommunity}
                    style={{
                      padding: '10px 18px', background: 'rgba(239,68,68,0.2)',
                      border: '1px solid rgba(239,68,68,0.3)', color: '#fca5a5',
                      borderRadius: '10px', cursor: 'pointer', fontWeight: '600', fontSize: '13px',
                    }}
                  >
                    Leave
                  </button>
                </>
              ) : (
                <button
                  onClick={handleJoinCommunity}
                  disabled={!permissions.canJoin}
                  style={{
                    padding: '12px 28px', background: permissions.canJoin ? 'white' : '#d1d5db',
                    color: permissions.canJoin ? '#4c1d95' : '#9ca3af',
                    border: 'none', borderRadius: '12px', cursor: permissions.canJoin ? 'pointer' : 'not-allowed',
                    fontWeight: '700', fontSize: '14px',
                  }}
                >
                  {permissions.canJoin
                    ? (detailCommunity.isMonetized ? `Join for $${(detailCommunity.subscriptionPrice || 0) / 100}/mo` : 'Join Community')
                    : 'Upgrade to Join'}
                </button>
              )}
              {isCommunityOwner && (
                <button
                  onClick={handleDeleteCommunity}
                  style={{
                    padding: '10px 16px', background: 'rgba(239,68,68,0.3)',
                    border: 'none', color: '#fca5a5', borderRadius: '10px',
                    cursor: 'pointer',
                  }}
                  aria-label="Delete community"
                >
                  <Trash2 size={16} />
                </button>
              )}
            </div>
          </div>
        </div>

        {/* Members Panel */}
        {showMembers && (
          <div style={{
            background: 'var(--bg-primary, white)', padding: '20px',
            borderRadius: '14px', border: '1px solid var(--border-color, #e5e7eb)',
            marginBottom: '20px',
          }}>
            <h3 style={{ fontWeight: '600', marginBottom: '16px', color: 'var(--text-primary, #111827)' }}>
              Members ({detailCommunity.memberCount || 0})
            </h3>
            {detailCommunity.members?.slice(0, 20).map((m: any) => (
              <div key={m._id} style={{
                display: 'flex', alignItems: 'center', gap: '10px',
                padding: '10px 0', borderBottom: '1px solid var(--border-color-light, #f3f4f6)',
                fontSize: '13px',
              }}>
                <Avatar name={m.user?.name || 'Unknown'} size={34} />
                <span style={{ flex: 1, color: 'var(--text-primary, #111827)' }}>
                  {m.user?.name || 'Unknown'}
                </span>
                {m.role === 'admin' && <Badge text="Admin" color="#7c3aed" bgColor="#ede9fe" icon={<Crown size={10} />} />}
                {m.role === 'moderator' && <Badge text="Mod" color="#2563eb" bgColor="#dbeafe" icon={<Shield size={10} />} />}
              </div>
            ))}
          </div>
        )}

        {/* Social Links / Settings Panel */}
        {showSettings && isCommunityOwner && (
          <div style={{ marginBottom: '20px' }}>
            <SocialLinksManager
              socialLinks={detailCommunity.socialLinks || []}
              userPlan={plan}
              onUpdate={handleUpdateSocialLinks}
            />
          </div>
        )}

        {/* Earnings Panel */}
        {showEarnings && detailCommunity.isMonetized && (
          <div style={{ marginBottom: '20px' }}>
            <EarningsDashboard
              communityId={detailCommunity._id}
              earningsSummary={earningsSummary}
              loading={earningsLoading}
            />
          </div>
        )}

        {/* Sort Bar */}
        <SortBar activeSort={activeSort} onSortChange={setActiveSort} />

        {/* Create Post */}
        {isMember && permissions.canPost && (
          <CreatePostInline
            communityId={detailCommunity._id}
            onSuccess={(post) => { addPost(post); success('Post published! 🚀'); }}
            onError={(msg) => error(msg)}
          />
        )}

        {/* Posts Feed */}
        {detailLoading ? (
          <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
            <Skeleton height="150px" />
            <Skeleton height="150px" />
          </div>
        ) : sortedPosts.length === 0 ? (
          <EmptyState
            icon={<MessageSquare size={48} />}
            title="No posts yet"
            description={isMember ? 'Be the first to share something!' : 'Join the community to see posts.'}
          />
        ) : (
          sortedPosts.map(post => (
            <PostCard
              key={post._id}
              post={post}
              currentUserId={currentUserId}
              isAdmin={isAdmin}
              isMember={isMember}
              onLike={handleLike}
              onComment={handleComment}
              onPin={handlePin}
              onDelete={handleDeletePost}
              onEdit={handleEditPost}
              onVote={handleVote}
              onRSVP={handleRSVP}
              onShare={handleShare}
              onReport={handleReport}
            />
          ))
        )}
      </div>
    );
  }

  // ─── RENDER: Community List View ────
  return (
    <div className="community-hub">
      {/* Toast Container */}
      <ToastContainer toasts={toasts} onRemove={removeToast} />

      {/* Hero Header */}
      <div className="community-hub__header">
        <h1>🌟 Communities</h1>
        <p>Connect, share, and grow with professionals in your niche</p>
        <button
  className="community-hub__cta-button"
  onClick={() => setShowCreateModal(true)}
  disabled={!permissions.canCreateCommunity && userPlan !== 'enterprise'}
  title={
    !permissions.canCreateCommunity && userPlan !== 'enterprise'
      ? userPlan === 'starter'
        ? 'Upgrade to Pro to create communities'
        : 'Community limit reached. Upgrade to Enterprise for unlimited.'
      : `Create a community (${permissions.communitiesRemaining} remaining)`
  }
>
  <Plus size={18} />
  Create Community
  {userPlan === 'enterprise' ? (
    <span style={{ fontSize: '11px', opacity: 0.8 }}>(unlimited)</span>
  ) : permissions.canCreateCommunity ? (
    <span style={{ fontSize: '11px', opacity: 0.8 }}>
      ({permissions.communitiesRemaining} left)
    </span>
  ) : null}
</button>
        {!permissions.canCreateCommunity && userPlan !== 'starter' && (
          <p style={{ fontSize: '12px', marginTop: '10px', opacity: 0.7, color: '#fbbf24' }}>
            Limit reached. <strong>Upgrade to Enterprise</strong> for unlimited communities.
          </p>
        )}
      </div>

      {/* Search & Filters */}
      <SearchFilterBar
        searchTerm={searchInput}
        onSearchChange={setSearchInput}
        activeFilter={activeFilter}
        onFilterChange={setActiveFilter}
        activeCategory={activeCategory}
        onCategoryChange={setActiveCategory}
      />

      {/* Error State */}
      {listError && (
        <div style={{
          padding: '16px', background: 'var(--toast-error-bg, #fef2f2)',
          color: 'var(--toast-error-color, #991b1b)', borderRadius: '12px',
          marginBottom: '20px', textAlign: 'center',
        }}>
          <AlertTriangle size={18} style={{ marginRight: '8px', verticalAlign: 'middle' }} />
          {listError}
          <button
            onClick={() => fetchCommunities({ reset: true })}
            style={{
              marginLeft: '12px', padding: '6px 14px', background: '#991b1b',
              color: 'white', border: 'none', borderRadius: '8px', cursor: 'pointer',
              fontSize: '12px',
            }}
          >
            Retry
          </button>
        </div>
      )}

      {/* Loading State */}
      {loading && communities.length === 0 ? (
        <div className="community-grid">
          {[...Array(6)].map((_, i) => (
            <div key={i} style={{ padding: '24px', background: 'var(--bg-primary, white)', borderRadius: '16px', border: '1px solid var(--border-color, #e5e7eb)' }}>
              <Skeleton width="52px" height="52px" borderRadius="14px" />
              <Skeleton width="60%" height="20px" style={{ marginTop: '14px' }} />
              <Skeleton width="40%" height="14px" style={{ marginTop: '8px' }} />
              <Skeleton width="80%" height="14px" style={{ marginTop: '10px' }} />
              <Skeleton width="50%" height="14px" style={{ marginTop: '14px' }} />
            </div>
          ))}
        </div>
      ) : communities.length === 0 ? (
        <EmptyState
          icon={<Users size={48} />}
          title="No communities found"
          description={
            searchInput || activeCategory !== 'all'
              ? 'Try adjusting your search or filters.'
              : 'Be the first to create a community!'
          }
          action={
            permissions.canCreateCommunity ? (
              <button
                className="community-hub__cta-button"
                onClick={() => setShowCreateModal(true)}
              >
                <Plus size={18} /> Create Community
              </button>
            ) : null
          }
        />
      ) : (
        <>
          <div className="community-grid">
            {communities.map((community, index) => (
              <CommunityCard
                key={community._id}
                community={community}
                onClick={() => setSelectedCommunity(community)}
              />
            ))}
          </div>

          {/* Infinite scroll trigger */}
          {hasMore && (
            <div ref={scrollTargetRef} style={{ padding: '40px', textAlign: 'center' }}>
              {loading && <div className="community-spinner" />}
            </div>
          )}
        </>
      )}

      {/* Create Community Modal */}
      <CreateCommunityModal
        isOpen={showCreateModal}
        onClose={() => setShowCreateModal(false)}
        onSubmit={handleCreateCommunity}
        permissions={permissions}
        userPlan={userPlan}
      />
    </div>
  );
};

// ============================================
// INLINE CREATE POST COMPONENT
// ============================================
const CreatePostInline: React.FC<{
  communityId: string;
  onSuccess: (post: Post) => void;
  onError: (msg: string) => void;
}> = React.memo(({ communityId, onSuccess, onError }) => {
  const [content, setContent] = useState('');
  const [isExpanded, setIsExpanded] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);

  const handleSubmit = async () => {
    if (!content.trim() || isSubmitting) return;
    setIsSubmitting(true);
    try {
      const res = await postApi.createPost(communityId, {
        type: 'post',
        content: content.trim(),
      });
      if (res.success && res.data) {
        onSuccess(res.data);
        setContent('');
        setIsExpanded(false);
      } else {
        onError(res.error || 'Failed to create post');
      }
    } catch (err: any) {
      onError(err.message || 'Something went wrong');
    } finally {
      setIsSubmitting(false);
    }
  };
 const handleImageUpload = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;
    const localUrl = URL.createObjectURL(file);
    setContent(prev => prev + `\n![Image](${localUrl})`);
    e.target.value = '';
}; 
  return (
    <div style={{
      background: 'var(--bg-primary, white)', padding: '20px',
      borderRadius: '14px', border: '1px solid var(--border-color, #e5e7eb)',
      marginBottom: '20px',
    }}>
      <textarea
        value={content}
        onChange={(e) => setContent(e.target.value)}
        onFocus={() => setIsExpanded(true)}
        placeholder="Share something with the community..."
        rows={isExpanded ? 4 : 2}
        style={{
          width: '100%', padding: '14px',
          border: '1.5px solid var(--border-color, #e5e7eb)',
          borderRadius: '12px', resize: 'vertical',
          fontSize: '14px', fontFamily: 'inherit',
          background: 'var(--bg-secondary, #f9fafb)',
          color: 'var(--text-primary, #111827)',
          outline: 'none', marginBottom: isExpanded ? '12px' : '0',
          transition: 'all 0.2s', boxSizing: 'border-box',
        }}
        onKeyDown={(e) => {
          if (e.key === 'Enter' && (e.ctrlKey || e.metaKey)) {
            e.preventDefault();
            handleSubmit();
          }
        }}
      />
      {isExpanded && (
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <div style={{ display: 'flex', gap: '8px' }}>
            <input
              ref={fileInputRef}
              type="file"
              accept="image/*"
              style={{ display: 'none' }}
              onChange={handleImageUpload}
            />
            <button
              onClick={() => fileInputRef.current?.click()}
              style={{
                background: 'none', border: 'none', cursor: 'pointer',
                color: 'var(--text-secondary, #6b7280)', padding: '8px',
                borderRadius: '8px', display: 'flex', alignItems: 'center', gap: '4px',
                fontSize: '12px',
              }}
              title="Upload image"
            >
              <ImageIcon size={16} /> Image
            </button>
            <span style={{ fontSize: '11px', color: 'var(--text-muted, #9ca3af)', display: 'flex', alignItems: 'center' }}>
              Ctrl+Enter to post
            </span>
          </div>
          <button
            onClick={handleSubmit}
            disabled={!content.trim() || isSubmitting}
            style={{
              padding: '10px 24px',
              background: content.trim() && !isSubmitting ? 'var(--accent, #7c3aed)' : '#d1d5db',
              color: 'white', border: 'none', borderRadius: '10px',
              cursor: content.trim() && !isSubmitting ? 'pointer' : 'not-allowed',
              fontWeight: '600', fontSize: '13px',
              display: 'flex', alignItems: 'center', gap: '6px',
            }}
          >
            {isSubmitting ? 'Posting...' : 'Publish'} <Send size={14} />
          </button>
        </div>
      )}
    </div>
  );
});

CreatePostInline.displayName = 'CreatePostInline';

// ============================================
// EXPORTS
// ============================================
export default CommunityHub;

// Re-export hooks from hooks.ts
export { 
  useAuth, 
  usePermissions, 
  useCommunities, 
  useCommunityDetail, 
  useToast, 
  useInfiniteScroll, 
  useDebounce, 
  useCommission, 
  useSocialPlatforms,
  useKeyboardShortcut,
} from './hooks';

// Re-export API from api.ts
export { 
  communityApi, 
  postApi, 
  commentApi, 
  uploadApi, 
  monetizationApi, 
  notificationApi,
  tokenManager,
  ApiError,
} from './api';

// Re-export all from types
export * from './types';

// Re-export all from utils
export * from './utils';